import { useState } from 'react';

interface Step {
  step: string;
  explanation: string;
  note?: string;
}

interface Example {
  problem: string;
  steps: Step[];
  answer: string;
}

interface PracticeQuestion {
  question: string;
  choices: string[];
  correct: number;
  explanation: string;
}

interface LessonCardProps {
  lessonNumber: number;
  title: string;
  objective: string;
  keyVocabulary: { term: string; definition: string }[];
  conceptExplanation: string;
  examples: Example[];
  practiceQuestions: PracticeQuestion[];
  tip: string;
}

export default function LessonCard({
  lessonNumber,
  title,
  objective,
  keyVocabulary,
  conceptExplanation,
  examples,
  practiceQuestions,
  tip,
}: LessonCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeExample, setActiveExample] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [activeStep, setActiveStep] = useState<Record<number, number>>({});

  const handleAnswer = (qIndex: number, choiceIndex: number) => {
    if (showResults) return;
    setSelectedAnswers((prev) => ({ ...prev, [qIndex]: choiceIndex }));
  };

  const score = Object.entries(selectedAnswers).filter(
    ([qi, ans]) => practiceQuestions[Number(qi)]?.correct === ans
  ).length;

  const revealStep = (exIdx: number, stepIdx: number) => {
    setActiveStep((prev) => ({ ...prev, [exIdx]: stepIdx }));
  };

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-violet-50 transition-colors duration-200 cursor-pointer"
      >
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex-shrink-0">
            <span className="text-white font-bold text-xl">{lessonNumber}</span>
          </div>
          <div>
            <p className="text-xs font-semibold text-violet-600 uppercase tracking-widest mb-1">Lesson {lessonNumber}</p>
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{objective}</p>
          </div>
        </div>
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-2xl text-violet-500`}></i>
        </div>
      </button>

      {isOpen && (
        <div className="px-8 pb-10 border-t border-gray-100">

          {/* Key Vocabulary */}
          <div className="mt-8">
            <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-book-open-line text-violet-500 text-xl"></i>
              Key Vocabulary
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {keyVocabulary.map((v, i) => (
                <div key={i} className="bg-violet-50 rounded-xl px-5 py-3 flex gap-3">
                  <span className="font-bold text-violet-700 whitespace-nowrap">{v.term}:</span>
                  <span className="text-gray-700 text-sm">{v.definition}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Concept Explanation */}
          <div className="mt-8 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6">
            <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <i className="ri-lightbulb-line text-violet-500 text-xl"></i>
              Concept Explanation
            </h4>
            <p className="text-gray-700 leading-relaxed text-base">{conceptExplanation}</p>
          </div>

          {/* Teacher Tip */}
          <div className="mt-5 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl px-6 py-4 flex gap-3">
            <i className="ri-star-line text-amber-500 text-xl flex-shrink-0 mt-0.5"></i>
            <p className="text-amber-800 text-sm leading-relaxed"><strong>Teacher's Tip:</strong> {tip}</p>
          </div>

          {/* Worked Examples */}
          <div className="mt-8">
            <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-pencil-ruler-2-line text-violet-500 text-xl"></i>
              Worked Examples
            </h4>
            <div className="flex gap-2 mb-6 flex-wrap">
              {examples.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveExample(i); setActiveStep({}); }}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeExample === i
                      ? 'bg-violet-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-violet-100'
                  }`}
                >
                  Example {i + 1}
                </button>
              ))}
            </div>

            {examples[activeExample] && (
              <div className="bg-gray-50 rounded-2xl p-6">
                <div className="bg-white rounded-xl px-6 py-4 mb-6 border-2 border-violet-200">
                  <p className="text-xs font-semibold text-violet-600 uppercase tracking-widest mb-1">Problem</p>
                  <p className="text-lg font-bold text-gray-900">{examples[activeExample].problem}</p>
                </div>

                <p className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-widest">Step-by-Step Solution</p>
                <div className="space-y-3">
                  {examples[activeExample].steps.map((s, si) => {
                    const revealed = (activeStep[activeExample] ?? -1) >= si;
                    return (
                      <div
                        key={si}
                        className={`rounded-xl p-5 border transition-all duration-300 ${
                          revealed
                            ? 'bg-white border-violet-200 shadow-sm'
                            : 'bg-gray-100 border-gray-200 opacity-50'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 flex items-center justify-center bg-violet-500 rounded-full flex-shrink-0">
                            <span className="text-white text-sm font-bold">{si + 1}</span>
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-gray-900 mb-1">{s.step}</p>
                            {revealed && (
                              <>
                                <p className="text-gray-700 text-sm leading-relaxed">{s.explanation}</p>
                                {s.note && (
                                  <p className="mt-2 text-xs text-violet-700 bg-violet-50 rounded-lg px-3 py-2 inline-block">
                                    💡 {s.note}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3 mt-5">
                  {(activeStep[activeExample] ?? -1) < examples[activeExample].steps.length - 1 ? (
                    <button
                      onClick={() => revealStep(activeExample, (activeStep[activeExample] ?? -1) + 1)}
                      className="px-6 py-2.5 bg-violet-500 text-white rounded-full text-sm font-semibold hover:bg-violet-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Reveal Next Step
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-xl px-5 py-3">
                      <i className="ri-checkbox-circle-fill text-violet-500 text-xl"></i>
                      <p className="font-bold text-violet-700">Answer: {examples[activeExample].answer}</p>
                    </div>
                  )}
                  <button
                    onClick={() => setActiveStep((prev) => ({ ...prev, [activeExample]: -1 }))}
                    className="px-4 py-2.5 bg-gray-200 text-gray-600 rounded-full text-sm font-semibold hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Practice Questions */}
          <div className="mt-10">
            <h4 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
              <i className="ri-question-line text-violet-500 text-xl"></i>
              Practice Questions
            </h4>
            <p className="text-sm text-gray-500 mb-5">Select your answer for each question, then click "Check My Answers".</p>
            <div className="space-y-6">
              {practiceQuestions.map((q, qi) => (
                <div key={qi} className="bg-gray-50 rounded-2xl p-6">
                  <p className="font-bold text-gray-900 mb-4">
                    <span className="text-violet-600 mr-2">Q{qi + 1}.</span>{q.question}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {q.choices.map((choice, ci) => {
                      let style = 'bg-white border-gray-200 text-gray-700 hover:border-violet-400';
                      if (showResults) {
                        if (ci === q.correct) style = 'bg-violet-50 border-violet-500 text-violet-800 font-semibold';
                        else if (selectedAnswers[qi] === ci) style = 'bg-red-50 border-red-400 text-red-700';
                        else style = 'bg-white border-gray-200 text-gray-400';
                      } else if (selectedAnswers[qi] === ci) {
                        style = 'bg-violet-50 border-violet-500 text-violet-800 font-semibold';
                      }
                      return (
                        <button
                          key={ci}
                          onClick={() => handleAnswer(qi, ci)}
                          className={`text-left px-5 py-3 rounded-xl border-2 transition-all duration-200 text-sm cursor-pointer whitespace-nowrap ${style}`}
                        >
                          <span className="font-bold mr-2">{String.fromCharCode(65 + ci)}.</span>{choice}
                        </button>
                      );
                    })}
                  </div>
                  {showResults && (
                    <div className={`mt-4 px-4 py-3 rounded-xl text-sm flex items-start gap-2 ${
                      selectedAnswers[qi] === q.correct ? 'bg-violet-50 text-violet-800' : 'bg-red-50 text-red-800'
                    }`}>
                      <i className={`${selectedAnswers[qi] === q.correct ? 'ri-checkbox-circle-fill text-violet-500' : 'ri-close-circle-fill text-red-500'} text-lg flex-shrink-0`}></i>
                      <p>{q.explanation}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-4 flex-wrap">
              {!showResults ? (
                <button
                  onClick={() => setShowResults(true)}
                  disabled={Object.keys(selectedAnswers).length < practiceQuestions.length}
                  className="px-8 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold rounded-full hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Check My Answers
                </button>
              ) : (
                <div className="flex items-center gap-4 flex-wrap">
                  <div className={`px-6 py-3 rounded-full font-bold text-lg ${
                    score === practiceQuestions.length ? 'bg-violet-100 text-violet-700' :
                    score >= practiceQuestions.length / 2 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    Score: {score} / {practiceQuestions.length}
                    {score === practiceQuestions.length && ' 🎉 Perfect!'}
                  </div>
                  <button
                    onClick={() => { setShowResults(false); setSelectedAnswers({}); }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-full hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
